import React from 'react'
import tailwindConfig from '../../tailwind.config'

const { screens } = tailwindConfig.theme
const screenPixels = {
    lg: parseInt(screens.lg),
    md: parseInt(screens.md),
    '2xs': parseInt(screens['2xs']),
}

const viewportTypeBreakpoints = {
    desktop: `(min-width: ${screenPixels.lg + 1}px`,
    tablet: `(min-width: ${screenPixels.md + 1}px) and (max-width: ${
        screenPixels.lg
    }px)`,
    landscapePhone: `(min-width: ${
        screenPixels['2xs'] + 1
    }px) and (max-width: ${screens.md}px)`,
    phone: `(max-width: ${screenPixels['2xs']}px)`,
}

type ViewportType = keyof typeof viewportTypeBreakpoints

function createMatchMediaMap(): Map<ViewportType, MediaQueryList> | undefined {
    if (typeof window === 'undefined') {
        return
    }

    const { phone, ...remainingBreakpoints } = viewportTypeBreakpoints
    const mediaQueryLists = new Map(
        Object.entries(remainingBreakpoints).map(([viewport, mediaQuery]) => {
            return [viewport, window.matchMedia(mediaQuery)]
        })
    ) as Map<ViewportType, MediaQueryList>
    mediaQueryLists.set('phone', window.matchMedia(phone))
    return mediaQueryLists
}

/**
 * Hook which returns viewport type of current viewport by comparing media queries with document
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
 */
export function useViewportType({
    // when true, the viewport type will be updated when the viewport is resized
    listen = false,
}: {
    listen?: boolean
} = {}): ViewportType | undefined {
    // mediaQueryLists is undefined on the server but always defined on the client
    const maybeMediaQueryLists = React.useMemo(createMatchMediaMap, [])
    const listenersRef = React.useRef<
        Map<string, (e: MediaQueryListEventMap['change']) => void>
    >(new Map())

    const [viewportType, setViewportType] = React.useState<
        ViewportType | undefined
    >()

    React.useEffect(() => {
        const mediaQueryLists = maybeMediaQueryLists!

        // Set the state variable for the simpler usage of this hook (without onChange),
        // which just returns the current viewport type and doesn't listen for browser
        // resize events
        const currentViewportType = Object.keys(viewportTypeBreakpoints).find(
            (viewport) => mediaQueryLists.get(viewport as ViewportType)!.matches
        )
        setViewportType(currentViewportType as ViewportType)

        if (!listen) {
            return
        }

        mediaQueryLists.forEach((mediaQueryList, viewport) => {
            const listener = (e: MediaQueryListEventMap['change']) => {
                if (e.matches) {
                    setViewportType(viewport as ViewportType)
                }
            }
            listenersRef.current.set(viewport, listener)
            mediaQueryList.addEventListener('change', listener)
        })

        const listeners = listenersRef.current
        return () => {
            mediaQueryLists.forEach((mediaQueryList, viewport) => {
                mediaQueryList.removeEventListener(
                    'change',
                    listeners.get(viewport)!
                )
            })
        }
    }, [listen, maybeMediaQueryLists])

    return viewportType
}
